import { DataAccess } from "../dataaccess/data.access";
import { Cache } from "../decorators/cache.decorator";
import { Timeout } from "../decorators/timeout.decorator";
import { Proto } from "../pojo/Proto";
import { ReservationI } from "../pojo/ReservationI";
import { PreferenceService } from "./preference.service";
import { S25Util } from "../util/s25-util";

export interface Evdetail {
    defn?: Evdetail.Defn;
    perm?: Evdetail.Perm;
    occur?: Evdetail.Occur;
    engine?: string;
    pref?: Evdetail.Pref;
    pubdate?: string;
}

// TODO: replace all these `item: any` with proper Item type
export namespace Evdetail {
    export type Defn = {
        panel?: Panel[];
    } & {
        [key in (typeof PanelPropsById)[keyof typeof PanelPropsById]]?: {
            item: any;
        };
    };

    export interface Perm {
        edit?: boolean;
        inlineEdit?: Proto.NumericalBoolean;
        inlineBoundEdit?: Proto.NumericalBoolean;
    }

    export interface Pref {
        isActive?: Proto.NumericalBoolean;
    }

    export interface Occur {
        prof?: Profile[];
    }

    export interface Profile {
        profId?: number;
        rsv?: ReservationI[];
        perm?: {
            delete?: Proto.NumericalBoolean;
        };
    }

    export interface Item {
        eventId?: number;
        itemTypeId: keyof typeof PanelPropsById;
        item?: any;
        [key: string]: any;
    }

    export interface Panel {
        typeId?: PanelType;
        item?: any;
    }

    const enum PanelType {
        General = 11,
        Description = 12,
        Comments = 13,
        InternalNotes = 14,
        ConfirmationText = 15,
        CustomAttributes = 16,
        Requirements = 17,
        TasksCompleted = 18,
        RelatedEvents = 19,
        BoundEventsReservations = 20,
        EventInfo = 21,
        FileList = 22,
    }
    type PanelPropId = `${PanelType}${number}`;

    export const PanelPropsById = {
        1101: "eventName",
        1102: "eventTitle",
        1103: "eventType",
        1104: "reference",
        1105: "alienUid",
        1106: "state",
        1107: "primaryOrganization",
        1108: "additionalOrganizations",
        1109: "contactRoles",
        1110: "categories",
        1111: "headCount",
        1201: "description",
        1301: "comments",
        1401: "internalNotes",
        1501: "confirmationText",
        1601: "customAttributes",
        1701: "requirements",
        1801: "tasksCompleted",
        1901: "relatedEvents",
        2001: "boundEventsReservations",
        2101: "eventOwner",
        2102: "creationDate",
        2103: "cabinet",
        2104: "folder",
        2201: "fileList",
    } as const satisfies Record<PanelPropId, string>;

    export const EditableDefaultItem: Partial<Record<PanelType, { item: Item }>> = {
        [PanelType.Description]: {
            item: {
                itemName: "",
                itemTypeId: 1201, //description item
                style: "evdd-item-description",
                type: false,
                desc: false,
            },
        },
    };
}

export class EvdetailService {
    @Timeout
    @Cache({ targetName: "EvdetailService", immutable: true })
    public static get(eventId: number): Promise<Evdetail | undefined> {
        return DataAccess.get(
            DataAccess.injectCaller(
                "/event/detail/evdetail.json?" + DataAccess.composeParam("event_id", eventId, true),
                "EvdetailService.get",
            ),
        ).then((data) => data?.evdetail);
    }

    /**
     * Add semantic nodes to the model for convenience and so we can move items around w/o respect to their panel (ie, item-centric design)
     */
    public static async getGenericModel(eventId: number): Promise<Evdetail> {
        const [model, editPref] = await Promise.all([
            EvdetailService.get(eventId),
            PreferenceService.getPreferences(["EventShowInlineEdit"]),
        ]);

        if (!model) {
            return {
                defn: {},
                perm: { inlineEdit: 0 },
            };
        }

        const modelCopy = S25Util.extend({}, model) as Evdetail;

        if (!modelCopy.defn?.panel) return modelCopy;

        const canEdit = model?.perm?.edit && model?.perm?.inlineEdit;
        const editPrefValue = editPref?.EventShowInlineEdit?.value === "T";
        const editable = canEdit && editPrefValue;
        modelCopy.perm ??= {};
        modelCopy.perm.inlineEdit = editable ? 1 : 0;

        // Loop panels and add each item in each panel to model
        for (const panel of modelCopy.defn.panel) {
            // If panel has no item and user has edit perms, try to set a default item for them to edit
            if (panel?.typeId && !panel.item && editable) {
                const defaultItem = S25Util.extend({}, Evdetail.EditableDefaultItem[panel.typeId]);
                if (defaultItem) {
                    defaultItem.eventId = eventId;
                    panel.item = defaultItem;
                }
            }

            if (!panel?.item) continue;

            // Loop items and add to model
            const items = S25Util.array.forceArray<Evdetail.Item>(panel.item);
            for (const obj of items) {
                obj.eventId ??= eventId;
                // Get semantic key for item type id
                const key = Evdetail.PanelPropsById[obj.itemTypeId];
                if (!key) continue;

                if (obj.item && modelCopy.defn[key]) {
                    // Create array of all items with the same itemTypeId for evddItemMaybeArray
                    modelCopy.defn[key].item = S25Util.array.forceArray(modelCopy.defn[key].item);
                    modelCopy.defn[key].item.push(obj);
                } else {
                    // Create single item for evddItemMaybeArray
                    modelCopy.defn[key] = { item: obj };
                }
            }
        }

        return modelCopy;
    }
}
